import _uniq from 'lodash/uniq';

import { FLOW_STEP_PATHS } from '@/constants';

import Ceo from '@/containers/Ceo';
import CompanyDetails from '@/containers/CompanyDetails';
import { STAKEHOLDER_ROLES_V2 } from '@/containers/withStakeholderLogic';

import { SoleTraderBlock } from '@/flow/blocks/kyc/common/SoleTrader.block';
import { not } from '@/flow/common';
import {
  hasCompanyDetails,
  hasRegistrationInformation,
  hasSubmittedStakeholderBlockUs,
  isEntityTypePublicListedCompany,
  isSoleTraderOrSoleProprietorship,
} from '@/flow/configs/selectors';

export class UsStakeholderBlock {
  constructor({ isManagedFlow, merchantCountry, prerequisite = [] }) {
    this.isManagedFlow = isManagedFlow;
    this.merchantCountry = merchantCountry;
    this.prerequisite = prerequisite;
  }

  static selectors = {
    hasCompanyDetails,
    hasRegistrationInformation,
    hasSubmittedStakeholderBlockUs,
    isEntityTypePublicListedCompany,
    isSoleTraderOrSoleProprietorship,
    ...SoleTraderBlock.selectors,
  };

  getSelectors() {
    return UsStakeholderBlock.selectors;
  }

  getSelectorToSubmitStep() {
    return 'hasSubmittedStakeholderBlockUs';
  }

  generateNextForPreviousStep() {
    return [
      {
        condition: 'isSoleTraderOrSoleProprietorship',
        path: FLOW_STEP_PATHS.SOLE_TRADER_PATH,
      },
      {
        condition: not('isSoleTraderOrSoleProprietorship'),
        path: FLOW_STEP_PATHS.COMPANY_DETAILS_PATH,
      },
    ];
  }

  generateBackForNextStep() {
    return [
      {
        condition: 'isSoleTraderOrSoleProprietorship',
        path: FLOW_STEP_PATHS.SOLE_TRADER_PATH,
      },
      {
        condition: not('isSoleTraderOrSoleProprietorship'),
        path: FLOW_STEP_PATHS.ARTIFICIAL_ULTIMATE_BENEFICIAL_OWNER_PATH,
      },
    ];
  }

  withGlobalPrerequisites(prerequisite = []) {
    return _uniq([...prerequisite, ...(this.prerequisite || [])]);
  }

  generateFlowConfig({ next, back }) {
    const soleTraderBlock = new SoleTraderBlock({
      isManagedFlow: this.isManagedFlow,
      back,
      next,
      prerequisite: this.prerequisite,
    });

    return [
      {
        id: 'company-details',
        path: FLOW_STEP_PATHS.COMPANY_DETAILS_PATH,
        prerequisite: this.withGlobalPrerequisites(['hasRegistrationInformation']),
        Container: CompanyDetails,
        back,
        next: FLOW_STEP_PATHS.ARTIFICIAL_ULTIMATE_BENEFICIAL_OWNER_PATH,
      },
      {
        id: 'ceo',
        path: FLOW_STEP_PATHS.ARTIFICIAL_ULTIMATE_BENEFICIAL_OWNER_PATH,
        prerequisite: this.withGlobalPrerequisites(['hasCompanyDetails']),
        Container: Ceo,
        // After MoMA fix their validations to allow non us countries to onboard without
        // stakeholders, uncomment this:
        // skip: this.merchantCountry !== 'US' && 'isEntityTypePublicListedCompany',
        back: FLOW_STEP_PATHS.COMPANY_DETAILS_PATH,
        next,
        extraProps: {
          defaultRole: STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT,
          roleConstraints: {
            [STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT]: {
              min: 1,
              max: 1,
            },
          },
        },
      },
      ...soleTraderBlock.generateFlowConfig(),
    ];
  }
}
